const isLocalHostname = (hostname) => ['localhost', '127.0.0.1'].includes(hostname);

const getBrowserOrigin = () => {
  if (typeof window === 'undefined') return 'http://localhost:3000';
  return window.location.origin;
};

const getBaseUrl = () => {
  const explicitBaseUrl = process.env.REACT_APP_API_BASE_URL;
  if (explicitBaseUrl) return explicitBaseUrl;

  const origin = getBrowserOrigin();
  if (typeof window !== 'undefined' && isLocalHostname(window.location.hostname)) {
    return 'http://localhost:3000/api';
  }

  return `${origin}/api`;
};

export const BASE_URL = getBaseUrl();
export const SERVER_URL = BASE_URL.replace(/\/api$/, '');
export const SOCKET_URL = SERVER_URL;

const EMPTY_UPLOAD_VALUES = new Set(['placeholder.jpg', 'null', 'undefined']);

function safeDecode(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function resolveUploadRef(relativePath) {
  if (!relativePath) return null;

  const raw = String(relativePath).trim();
  if (!raw || EMPTY_UPLOAD_VALUES.has(raw.toLowerCase())) return null;

  if (/^https?:\/\//i.test(raw)) {
    const marker = '/uploads/';
    const markerIndex = raw.indexOf(marker);
    if (markerIndex === -1) return { externalUrl: raw };

    const origin = raw.match(/^(https?:\/\/[^/]+)/i)?.[1] || SERVER_URL;
    const path = raw.slice(markerIndex + marker.length).split(/[?#]/)[0];
    return { baseUrl: origin, path: safeDecode(path).replace(/^\/+/, '') };
  }

  const cleaned = raw.replace(/^\/+/, '');
  return {
    baseUrl: SERVER_URL,
    path: cleaned.startsWith('uploads/') ? cleaned.slice('uploads/'.length) : cleaned,
  };
}

function buildUploadUrl(ref, path) {
  return `${ref.baseUrl || SERVER_URL}/uploads/${path.replace(/^\/+/, '')}`;
}

function thumbPath(relativePath, legacy = false) {
  const ref = resolveUploadRef(relativePath);
  if (!ref?.path) return null;

  const dot = ref.path.lastIndexOf('.');
  const base = dot > 0 ? ref.path.slice(0, dot) : ref.path;
  const ext = dot > 0 ? ref.path.slice(dot) : '';
  return legacy ? `${base}_thumb${ext}` : `${base}_thumb.jpg`;
}

export function uploadUrl(relativePath) {
  const ref = resolveUploadRef(relativePath);
  if (!ref) return null;
  if (ref.externalUrl) return ref.externalUrl;
  return buildUploadUrl(ref, ref.path);
}

export function thumbUrl(relativePath) {
  const ref = resolveUploadRef(relativePath);
  const path = thumbPath(relativePath);
  return ref?.path && path ? buildUploadUrl(ref, path) : null;
}

export function legacyThumbUrl(relativePath) {
  const ref = resolveUploadRef(relativePath);
  const path = thumbPath(relativePath, true);
  return ref?.path && path ? buildUploadUrl(ref, path) : null;
}

export function thumbUrls(relativePath) {
  return [...new Set([thumbUrl(relativePath), legacyThumbUrl(relativePath)].filter(Boolean))];
}

export async function uploadProfilePhoto(file, token = null) {
  const formData = new FormData();
  formData.append('photo', file);

  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}/uploads/profile-photo`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const text = await res.text();

  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Upload failed (${res.status})`);
  }

  if (!res.ok || !json.success) throw new Error(json.message || 'Upload failed');
  return json;
}

export async function uploadSchoolLogo(file, token = null) {
  const formData = new FormData();
  formData.append('logo', file);

  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}/uploads/school-logo`, {
    method: 'POST',
    headers,
    body: formData,
  });
  const text = await res.text();

  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Upload failed (${res.status})`);
  }

  if (!res.ok || !json.success) throw new Error(json.message || 'Upload failed');
  return json;
}

export async function apiFetch(path, options = {}, token = null) {
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
  const text = await res.text();

  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Server error (${res.status})`);
  }

  if (!res.ok) {
    // Attach status + payload so callers can act on structured 409s
    // (e.g. offering to reuse an existing contact account).
    const error = new Error(json.message || 'Request failed');
    error.status = res.status;
    error.data = json;
    throw error;
  }
  return json;
}

export async function apiFetchBlob(path, options = {}, token = null) {
  const headers = { ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(`${BASE_URL}${path}`, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    let message = `Request failed (${res.status})`;
    try {
      message = JSON.parse(text).message || message;
    } catch {
      // Media errors may not contain JSON; keep the status-based message.
    }

    const error = new Error(message);
    error.status = res.status;
    throw error;
  }

  return res.blob();
}
